import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";
import { debounce } from "lodash";
import * as React from "react";
import { USERS_KEYS } from "~/shared/api/users/users.queries";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/shared/components/ds/AlertDialog";
import { ButtonLink } from "~/shared/components/ds/ButtonLink";
import { toast } from "~/shared/components/ds/Toast/Toast";

// Time before token expiry to show the dialog (in seconds)
const WARNING_BEFORE_TIMEOUT = 5 * 60; // 5 minutes
// How often to check if session is nearing expiration (in milliseconds)
const CHECK_INTERVAL = 60 * 1000; // 1 minute
// Debounce delay for handling user activity (in milliseconds)
const ACTIVITY_DEBOUNCE_DELAY = 1000; // 1 second
// Session refresh buffer (in seconds) - refresh when within this time of showing warning
const SESSION_REFRESH_BUFFER = 5 * 60; // 5 minutes before warning would appear

// Session states
const SESSION_STATE = {
  ACTIVE: "active",
  WARNING: "warning",
  EXPIRED: "expired",
} as const;

type SessionState = (typeof SESSION_STATE)[keyof typeof SESSION_STATE];

export function SessionTimeoutManager() {
  const [dialogState, setDialogState] = React.useState<{
    show: boolean;
    state: SessionState;
    expiryTime: number | null;
  }>({
    show: false,
    state: SESSION_STATE.ACTIVE,
    expiryTime: null,
  });
  const queryClient = useQueryClient();

  // Session refresh mutation
  const sessionRefreshMutation = useMutation({
    mutationFn: async () => {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      return tokens?.accessToken.payload;
    },
    onSuccess: (payload) => {
      // Invalidate user query to refresh user data
      queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
      setDialogState((prev) => ({
        ...prev,
        show: false,
        state: SESSION_STATE.ACTIVE,
        expiryTime: payload?.exp ?? null,
      }));
    },
    onError: (error) => {
      toast.api.error({ error, title: "Unable to extend your session. You may need to log in again." });
      setDialogState((prev) => ({ ...prev, state: SESSION_STATE.EXPIRED, expiryTime: null }));
    },
  });

  // Check session status periodically
  const checkSession = React.useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const { accessToken } = tokens ?? {};
      const { payload } = accessToken ?? {};

      if (payload?.exp == null) return;

      const currentTime = Math.round(new Date().getTime() / 1000);
      const timeToExpiry = payload.exp - currentTime;

      if (timeToExpiry <= 0) {
        setDialogState({
          show: true,
          state: SESSION_STATE.EXPIRED,
          expiryTime: null,
        });
        return;
      }

      if (timeToExpiry <= WARNING_BEFORE_TIMEOUT) {
        setDialogState({
          show: true,
          state: SESSION_STATE.WARNING,
          expiryTime: payload.exp,
        });
      }
    } catch (err) {
      setDialogState({
        show: true,
        state: SESSION_STATE.EXPIRED,
        expiryTime: null,
      });
    }
  }, []);

  // End session
  const closeDialog = React.useCallback(() => {
    setDialogState((prev) => ({ ...prev, show: false }));
    // Let the session expire naturally
  }, []);

  // Set up periodic checks
  React.useEffect(() => {
    const intervalId = setInterval(checkSession, CHECK_INTERVAL);

    // Initial check
    checkSession();

    return () => clearInterval(intervalId);
  }, [checkSession]);

  // Track user activity - silently refresh session while user is active
  React.useEffect(() => {
    const activityEvents = ["mousedown", "keydown"];

    const handleUserActivity = debounce(async () => {
      // Don't refresh if we're already showing the dialog
      if (dialogState.show) return;

      try {
        const { tokens } = await fetchAuthSession();
        const { accessToken } = tokens ?? {};
        const { payload } = accessToken ?? {};

        if (payload?.exp == null) return;

        const currentTime = Math.round(new Date().getTime() / 1000);
        const timeToExpiry = payload.exp - currentTime;

        /**
         * If we're approaching the warning threshold (which is our warning threshold + a buffer), preemptively refresh
         * the token. This creates a sliding window based on user activity and keeps the session alive.
         */
        if (timeToExpiry < WARNING_BEFORE_TIMEOUT + SESSION_REFRESH_BUFFER) {
          await fetchAuthSession({ forceRefresh: true });
          queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
        }
      } catch (err) {
        // Do nothing if the session is already expired
      }
    }, ACTIVITY_DEBOUNCE_DELAY);

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // Clean up event listeners
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      // Cancel any pending debounced calls
      handleUserActivity.cancel();
    };
  }, [dialogState.show, queryClient]);

  // Time remaining calculation
  const timeRemaining = dialogState.expiryTime
    ? Math.max(0, Math.floor((dialogState.expiryTime * 1000 - Date.now()) / 1000 / 60))
    : 0;

  // Dialog content based on session state
  const dialogContent = {
    [SESSION_STATE.WARNING]: {
      title: "Session About to Expire",
      description: `Your session will expire in approximately ${timeRemaining} minutes. Would you like to extend your session?`,
      actions: (
        <>
          <AlertDialogCancel onClick={closeDialog} disabled={sessionRefreshMutation.isPending}>
            Close
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => sessionRefreshMutation.mutate()}
            disabled={sessionRefreshMutation.isPending}
            isLoading={sessionRefreshMutation.isPending}
          >
            Extend Session
          </AlertDialogAction>
        </>
      ),
    },
    [SESSION_STATE.EXPIRED]: {
      title: "Session Expired",
      description: "Your session has expired. Please log in again to continue.",
      actions: (
        <>
          <AlertDialogCancel onClick={closeDialog} disabled={sessionRefreshMutation.isPending}>
            Close
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <ButtonLink to="/login">Log In Again</ButtonLink>
          </AlertDialogAction>
        </>
      ),
    },
    [SESSION_STATE.ACTIVE]: {
      title: "",
      description: "",
      actions: null,
    },
  };

  const currentDialogContent = dialogContent[dialogState.state];

  return (
    <AlertDialog open={dialogState.show} onOpenChange={(open) => setDialogState((prev) => ({ ...prev, show: open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{currentDialogContent.title}</AlertDialogTitle>
          <AlertDialogDescription>{currentDialogContent.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>{currentDialogContent.actions}</AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
